/**
 * SeasonalBannerSection — landing-page container for the seasonal marketing strip.
 *
 * Business rules:
 * - Renders nothing (no reserved space) when banner is null/undefined or headline
 *   is empty. Image is optional — tint + copy paint as soon as JSON arrives
 *   (Task #1067); artwork fades in inside SeasonalBanner.
 * - Banner is not dismissible — it stays visible whenever the API returns one.
 * - Tracks shown / clicked via marketingTracking (GTM production only).
 * - Holiday CTA deep-links Explore to the holiday's event-type tab (same contract
 *   as seasonal nurture emails); evergreen navigates to ctaRoute.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PublicSeasonalBanner } from '../../services/publicTemplatesService';
import { resolvePublicImageUrl } from '../../services/publicTemplatesService';
import {
  trackSeasonalBannerClicked,
  trackSeasonalBannerShown,
} from '../../services/marketingTracking';
import {
  SEASONAL_BANNER_DEFAULT_CTA_ROUTE,
  SEASONAL_BANNER_SOURCE_HOLIDAY,
} from '../../constants/seasonalBanner';
import {
  LANDING_EXPLORE_EVENT_TYPE_QUERY_KEY,
  LANDING_EXPLORE_SECTION_ID,
} from '../../constants/landingExplore';
import { SeasonalBanner } from './SeasonalBanner';

export { LANDING_EXPLORE_SECTION_ID } from '../../constants/landingExplore';

export interface SeasonalBannerSectionProps {
  /** Active banner from landing-lcp or GET /public/seasonal-banner; null hides the strip. */
  banner: PublicSeasonalBanner | null | undefined;
}

/**
 * Landing container that wires CTA / analytics for the presentational SeasonalBanner.
 */
export function SeasonalBannerSection({ banner }: SeasonalBannerSectionProps) {
  const navigate = useNavigate();
  const shownTrackedRef = useRef(false);

  const imageUrl = useMemo(() => {
    if (!banner?.imageUrl) return '';
    return resolvePublicImageUrl(banner.imageUrl);
  }, [banner?.imageUrl]);

  useEffect(() => {
    shownTrackedRef.current = false;
  }, [banner]);

  // Impression fires when the shell is visible (copy on tint), not when art loads.
  useEffect(() => {
    if (!banner?.headline?.trim()) return;
    if (shownTrackedRef.current) return;

    shownTrackedRef.current = true;
    trackSeasonalBannerShown(banner.source);
  }, [banner]);

  if (!banner || !banner.headline?.trim()) {
    return null;
  }

  const handleCta = () => {
    trackSeasonalBannerClicked(banner.source);

    if (banner.source === SEASONAL_BANNER_SOURCE_HOLIDAY) {
      const slug = banner.slug?.trim();
      if (slug) {
        // Same deep-link shape as OccasionSchedule nurture CTAs.
        navigate({
          pathname: '/',
          search: `?${LANDING_EXPLORE_EVENT_TYPE_QUERY_KEY}=${encodeURIComponent(slug)}`,
          hash: `#${LANDING_EXPLORE_SECTION_ID}`,
        });
        return;
      }

      const explore = document.getElementById(LANDING_EXPLORE_SECTION_ID);
      if (explore) {
        explore.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    const route = banner.ctaRoute?.trim() || SEASONAL_BANNER_DEFAULT_CTA_ROUTE;
    navigate(route);
  };

  return (
    <SeasonalBanner
      imageUrl={imageUrl || null}
      headline={banner.headline}
      subtext={banner.subtext}
      color={banner.color}
      daysUntil={banner.daysUntil}
      name={banner.name}
      onCta={handleCta}
    />
  );
}

export default SeasonalBannerSection;
